'use client';

/**
 * Cloudflare Turnstile widget (bot protection for self-serve provisioning).
 *
 * Interaction-only + explicit execution: the widget renders nothing until
 * Cloudflare needs an interaction, and only solves when `execute()` runs so
 * the token is fresh (tokens last 300 s, one use).
 *
 * Two modes:
 * - Callback mode (`onVerify` provided, signup form): the parent calls
 *   `execute()` on submit; `onVerify(token)` continues the flow.
 * - Passive mode (no callbacks, first-run/setup screens): the widget
 *   executes itself after render and the parent reads the token
 *   imperatively at submit time, resetting after every attempt.
 *
 * Fails closed: no site key or script failure blocks submission with a clear
 * message instead of silently skipping verification.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      getResponse: (widgetId?: string) => string;
      execute: (widgetId?: string) => void;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    __turnstileLoaded?: boolean;
  }
}

export interface TurnstileHandle {
  getToken: () => string;
  reset: () => void;
  execute: () => void;
}

interface TurnstileWidgetProps {
  action?: string;
  onVerify?: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '').trim();

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Turnstile script failed')));
        return;
      }
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Turnstile script failed'));
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ action = 'workspace', onVerify, onError, onExpire }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    // SITE_KEY is a build-time constant: safe to evaluate during render.
    const [failed, setFailed] = useState(() => !SITE_KEY);
    // Collapsed slot (spec §02): 0px closed, 65px open, 280ms ease. Opens
    // only when Cloudflare actually renders a challenge iframe.
    const [open, setOpen] = useState(false);

    // Latest callbacks without re-rendering the widget when they change.
    const callbacksRef = useRef({ onVerify, onError, onExpire });
    // Callback mode (signup) executes on demand; passive mode (first-run)
    // executes itself so getToken() keeps working as before.
    const callbackModeRef = useRef(onVerify !== undefined);
    useEffect(() => {
      callbacksRef.current = { onVerify, onError, onExpire };
      callbackModeRef.current = onVerify !== undefined;
    });

    const doExecute = () => {
      if (!window.turnstile || !widgetIdRef.current) {
        // Fail closed: a submit waiting on this widget must hear back.
        callbacksRef.current.onError?.();
        return;
      }
      try {
        window.turnstile.execute(widgetIdRef.current);
      } catch {
        callbacksRef.current.onError?.();
      }
    };

    useImperativeHandle(ref, () => ({
      getToken: () => {
        if (!window.turnstile || !widgetIdRef.current) return '';
        try {
          return window.turnstile.getResponse(widgetIdRef.current) || '';
        } catch {
          return '';
        }
      },
      reset: () => {
        if (!window.turnstile || !widgetIdRef.current) return;
        try {
          window.turnstile.reset(widgetIdRef.current);
          // Explicit execution never re-solves by itself: passive mode
          // re-arms so the next getToken() can succeed; callback mode
          // waits for the next submit.
          if (!callbackModeRef.current) {
            window.turnstile.execute(widgetIdRef.current);
          }
        } catch {
          // ignore: widget may already be gone
        }
      },
      execute: () => {
        doExecute();
      },
    }), []);

    useEffect(() => {
      if (!SITE_KEY) {
        return;
      }
      let cancelled = false;
      loadScript()
        .then(() => {
          if (cancelled || !containerRef.current || !window.turnstile) return;
          try {
            widgetIdRef.current = window.turnstile.render(containerRef.current, {
              sitekey: SITE_KEY,
              action,
              theme: 'light',
              appearance: 'interaction-only',
              execution: 'execute',
              size: 'flexible',
              callback: (token: unknown) => {
                if (typeof token === 'string' && token) {
                  callbacksRef.current.onVerify?.(token);
                }
              },
              'error-callback': () => {
                callbacksRef.current.onError?.();
              },
              'expired-callback': () => {
                if (callbackModeRef.current) {
                  callbacksRef.current.onExpire?.();
                } else if (widgetIdRef.current && window.turnstile) {
                  // Passive mode: re-arm so getToken() recovers by itself.
                  try {
                    window.turnstile.execute(widgetIdRef.current);
                  } catch {
                    // ignore: next submit surfaces the missing token
                  }
                }
              },
              'timeout-callback': () => {
                callbacksRef.current.onError?.();
              },
            });
            if (!callbackModeRef.current) {
              window.turnstile.execute(widgetIdRef.current);
            }
          } catch {
            if (!cancelled) setFailed(true);
          }
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
      return () => {
        cancelled = true;
        const widgetId = widgetIdRef.current;
        widgetIdRef.current = null;
        if (widgetId && window.turnstile) {
          try {
            window.turnstile.remove(widgetId);
          } catch {
            // ignore
          }
        }
      };
    }, [action]);

    // Open the slot only while a challenge iframe is actually rendered.
    useEffect(() => {
      if (failed) return;
      const el = containerRef.current;
      if (!el || typeof MutationObserver === 'undefined') return;
      const update = () => setOpen(el.querySelector('iframe') !== null);
      update();
      const observer = new MutationObserver(update);
      observer.observe(el, { childList: true, subtree: true });
      return () => observer.disconnect();
    }, [failed]);

    if (!SITE_KEY || failed) {
      return (
        <p role="alert" className="text-sm text-[var(--ink)]">
          Bot verification failed to load. Please check your connection and refresh the page.
        </p>
      );
    }
    return (
      <div style={{ height: open ? 65 : 0, overflow: 'hidden', transition: 'height 280ms ease' }}>
        <div ref={containerRef} aria-label="Bot verification" />
      </div>
    );
  }
);
